import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const SupplierAdd = ({ open, onClose, token }) => {
  const { t, i18n } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/suppliers", token);
  const currentLanguage = (
    i18n.resolvedLanguage ||
    i18n.language ||
    localStorage.getItem("i18nextLng") ||
    "en"
  ).split("-")[0];
  const isRTL = ["ps", "dr"].includes(currentLanguage);
  const fieldDirection = isRTL ? "rtl" : "ltr";
  const fieldTextAlign = isRTL ? "right" : "left";
  const getTextFieldProps = ({
    inputProps = {},
    InputLabelProps = {},
    sx = {},
  } = {}) => ({
    inputProps: {
      ...inputProps,
      dir: fieldDirection,
      style: {
        ...(inputProps.style || {}),
        textAlign: fieldTextAlign,
      },
    },
    InputLabelProps: {
      ...InputLabelProps,
      sx: {
        direction: fieldDirection,
        right: isRTL ? 24 : "auto",
        left: isRTL ? "auto" : undefined,
        transformOrigin: isRTL ? "top right" : "top left",
        ...(InputLabelProps.sx || {}),
      },
    },
    sx: {
      direction: fieldDirection,
      "& .MuiOutlinedInput-root": {
        direction: fieldDirection,
      },
      "& .MuiInputBase-input": {
        textAlign: fieldTextAlign,
      },
      "& .MuiInputLabel-root": {
        textAlign: fieldTextAlign,
      },
      ...sx,
    },
  });
  const selectProps = {
    MenuProps: {
      PaperProps: {
        sx: { direction: fieldDirection, textAlign: fieldTextAlign },
      },
    },
  };
  // Fetch shops for selection
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );

  const shops = shopsData?.results || [];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [selectedShop, setSelectedShop] = useState("");

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("email", email.trim());
    formData.append("contact", contact.trim());
    formData.append("address", address.trim());
    formData.append("shop", selectedShop);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setName("");
      setEmail("");
      setContact("");
      setAddress("");
      setSelectedShop("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir={fieldDirection}>
      <DialogTitle>{t("supplier.addSupplier")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1, direction: fieldDirection }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("supplier.supplierName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("supplier.supplierEmail")}
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("supplier.supplierContact")}
            type="text"
            fullWidth
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            margin="dense"
            label={t("supplier.supplierAddress")}
            type="text"
            fullWidth
            multiline
            rows={3}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            {...getTextFieldProps()}
          />
          <TextField
            select
            margin="dense"
            label={t("supplier.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
            SelectProps={selectProps}
            {...getTextFieldProps()}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("supplier.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim() || !selectedShop}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("supplier.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierAdd;
